import { apiClient, ApiError } from './client';
import type { OneDriveMigration, OneDriveSharedItem } from '@/types/oscar';

/**
 * OneDrive-to-Vault migration API (PR-33). Reads are open to any authenticated
 * Oscar user; retry/trigger are oscar-admin only (enforced server-side).
 */

/** GET /api/offboarding/onedrive-migrations — every migration row, newest EligibleSince first. */
export function listOneDriveMigrations(signal?: AbortSignal): Promise<OneDriveMigration[]> {
  return apiClient.get<OneDriveMigration[]>('/api/offboarding/onedrive-migrations', signal);
}

/** GET .../{personId}/shared-items — the shared-with manifest for one person's migration. */
export function getOneDriveSharedItems(personId: number, signal?: AbortSignal): Promise<OneDriveSharedItem[]> {
  return apiClient.get<OneDriveSharedItem[]>(`/api/offboarding/onedrive-migrations/${personId}/shared-items`, signal);
}

/**
 * GET .../by-person/{personId} — single-person lookup for the People-screen
 * indicator. Resolves to `null` (not an error) when no migration row exists yet,
 * so the caller can render a "Not migrated" state.
 */
export async function getMigrationForPerson(personId: number, signal?: AbortSignal): Promise<OneDriveMigration | null> {
  try {
    return await apiClient.get<OneDriveMigration>(`/api/offboarding/onedrive-migrations/by-person/${personId}`, signal);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/** POST .../{personId}/retry — reset a Failed row to Pending (oscar-admin). */
export function retryOneDriveMigration(personId: number): Promise<unknown> {
  return apiClient.post(`/api/offboarding/onedrive-migrations/${personId}/retry`);
}

/** POST .../{personId}/trigger — manual/historic trigger, skips the 7-day wait (oscar-admin). */
export function triggerOneDriveMigration(personId: number): Promise<unknown> {
  return apiClient.post(`/api/offboarding/onedrive-migrations/${personId}/trigger`);
}

/** Summary returned by the copy-processor run. */
export interface OneDriveCopyRunResult {
  RanSync: boolean;
  Processed: number;
  Copied: number;
  Failed: number;
  Skipped: number;
  /** Copies still running when an on-demand run hit its time budget — the row
   *  stays Pending and the scheduled run finishes it. Always 0 for the timer. */
  InProgress: number;
  Note?: string | null;
}

/** POST /api/sync/onedrive-migrations/copy — run the copy processor now (oscar-admin).
 *  On-demand runs are time-bounded (~35s); large copies still running are left for
 *  the scheduled run. Processes due rows (Manual immediately; AutoScan ≥ 7 days). */
export function runOneDriveMigrationCopy(): Promise<OneDriveCopyRunResult> {
  return apiClient.post<OneDriveCopyRunResult>('/api/sync/onedrive-migrations/copy');
}
